import React, { useState, useRef } from 'react';
import csvService from '../../services/csvService';
import kpiCategoryService, { ImportResult } from '../../services/kpiCategoryService';
import { parseKPIImportData, ParsedKPIRow } from '../../utils/csvTemplateGenerator';
import { useTheme } from '../../contexts/ThemeContext';
import { KPI_TIME_TYPES } from '../../constants/kpiTimeTypes';
import './KPIImportModal.css';

interface KPIImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (results: ImportResult) => void;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'complete' | 'error';

const KPIImportModal: React.FC<KPIImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const { theme } = useTheme();
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedKPIRow[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset modal state when opened/closed
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep('upload');
      setSelectedFile(null);
      setParsedData([]);
      setImportProgress(0);
      setError(null);
      setImportResults(null);
    }
  }, [isOpen]);

  const handleFileSelect = async (file: File) => {
    try {
      setSelectedFile(file);
      setError(null);
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Please select a CSV file');
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }
      
      // Parse CSV
      const csvFile = await csvService.parseCSV(file);
      
      // Construct data array from columns and rows
      const csvData = [csvFile.columns, ...csvFile.rows];
      console.log('CSV data constructed:', csvData.slice(0, 2)); // Log first 2 rows for debugging
      
      const importData = parseKPIImportData(csvData);
      
      setParsedData(importData);
      setCurrentStep('preview');
      
    } catch (err: any) {
      setError(err.message || 'Failed to parse CSV file');
      setCurrentStep('error');
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    try {
      setCurrentStep('importing');
      setImportProgress(0);
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setImportProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const results = await kpiCategoryService.importKPICategories(selectedFile);
      
      clearInterval(progressInterval);
      setImportProgress(100);
      setImportResults(results);
      
      setTimeout(() => {
        setCurrentStep('complete');
      }, 500);
      
    } catch (err: any) {
      setError(err.message || 'Import failed');
      setCurrentStep('error');
    }
  };

  const handleComplete = () => {
    if (importResults) {
      onImportComplete(importResults);
    }
    onClose();
  };

  const handleTryAgain = () => {
    setCurrentStep('upload');
    setError(null);
    setSelectedFile(null);
    setParsedData([]);
  };

  const renderUploadStep = () => (
    <div className="import-step upload-step">
      <h3>Upload CSV File</h3>
      <p className="step-description">
        Select a CSV file containing your KPI categories and KPIs. 
        <br />Use our template to ensure proper formatting.
      </p>
      
      <div 
        className="upload-area" 
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const files = e.dataTransfer.files;
          if (files.length > 0) {
            handleFileSelect(files[0]);
          }
        }}
      >
        <div className="upload-icon">📤</div>
        <p>Click to select CSV file or drag and drop</p>
        <p className="upload-hint">Maximum file size: 5MB</p>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
        style={{ display: 'none' }}
      />
      
      <div className="upload-requirements">
        <h4>Required CSV Format:</h4>
        <ul>
          <li>Category Name</li>
          <li>KPI Name</li>
          <li>KPI Code</li>
          <li>Measurement Type (Value, Units, Percentage, Hours)</li>
          <li>Description (optional)</li>
          <li>Type (MTD, YTD, R3, R6, R12, QTD, WTD, CYTD, FYTD, PY, PM, PQ)</li>
        </ul>
        
        <div style={{
          marginTop: '15px',
          padding: '12px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderLeft: '3px solid #3b82f6',
          fontSize: '0.85rem',
          borderRadius: '4px'
        }}>
          <div style={{fontWeight: 'bold', marginBottom: '8px'}}>💡 Time Period Abbreviations:</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '6px',
            fontSize: '0.8rem'
          }}>
            <span>MTD - Monthly</span>
            <span>YTD - Year to Date</span>
            <span>QTD - Quarter to Date</span>
            <span>WTD - Week to Date</span>
            <span>R3 - Rolling 3M</span>
            <span>R6 - Rolling 6M</span>
            <span>R12 - Rolling 12M</span>
            <span>CYTD - Calendar YTD</span>
            <span>FYTD - Fiscal YTD</span>
            <span>PY - Prior Year</span>
            <span>PM - Prior Month</span>
            <span>PQ - Prior Quarter</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    const categoryCount = new Set(parsedData.map(row => row.categoryName)).size;
    const kpiCount = parsedData.length;

    return (
      <div className="import-step preview-step">
        <h3>Import Preview</h3>
        
        <div className="preview-summary">
          <div className="summary-item">
            <span className="summary-label">Categories:</span>
            <span className="summary-value">{categoryCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">KPIs:</span>
            <span className="summary-value">{kpiCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">File:</span>
            <span className="summary-value">{selectedFile?.name}</span>
          </div>
        </div>
        
        <div className="preview-table-container">
          <table className="preview-table">
            <thead>
              <tr>
                <th>Category Name</th>
                <th>KPI Name</th>
                <th>KPI Code</th>
                <th>Measurement Type</th>
                <th>Time Period Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {parsedData.slice(0, 20).map((row, index) => (
                <tr key={index}>
                  <td>{row.categoryName}</td>
                  <td>{row.kpiName}</td>
                  <td className="kpi-code">{row.kpiCode}</td>
                  <td className="measurement-type">{row.measurementType}</td>
                  <td className="type">
                    {row.type}
                    {KPI_TIME_TYPES[row.type] && (
                      <span style={{fontSize: '0.8rem', color: '#888', marginLeft: '5px'}}>
                        ({KPI_TIME_TYPES[row.type].abbreviation})
                      </span>
                    )}
                  </td>
                  <td className="description">{row.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {parsedData.length > 20 && (
            <p className="preview-note">... and {parsedData.length - 20} more rows</p>
          )}
        </div>
        
        <div className="preview-actions">
          <button className="cancel-btn" onClick={() => setCurrentStep('upload')}>
            Back
          </button>
          <button className="confirm-btn" onClick={handleConfirmImport}>
            Import {kpiCount} KPIs
          </button>
        </div>
      </div>
    );
  };

  const renderImportingStep = () => (
    <div className="import-step importing-step">
      <h3>Importing KPIs...</h3>
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${importProgress}%` }}
          />
        </div>
        <p className="progress-text">{importProgress}% complete</p>
      </div>
      <p className="importing-message">
        Processing your KPI data. This may take a few moments...
      </p>
    </div>
  );

  const renderCompleteStep = () => {
    if (!importResults) return null;

    const { success, categoriesCreated, categoriesUpdated, kpisCreated, errors, warnings } = importResults;

    return (
      <div className="import-step complete-step">
        <div className={`completion-header ${success ? 'success' : 'partial-success'}`}>
          <div className="completion-icon">
            {success ? '✅' : '⚠️'}
          </div>
          <h3>{success ? 'Import Successful!' : 'Import Completed with Issues'}</h3>
        </div>

        <div className="import-summary">
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-number">{categoriesCreated}</div>
              <div className="summary-label">Categories Created</div>
            </div>
            <div className="summary-card">
              <div className="summary-number">{categoriesUpdated}</div>
              <div className="summary-label">Categories Updated</div>
            </div>
            <div className="summary-card">
              <div className="summary-number">{kpisCreated}</div>
              <div className="summary-label">KPIs Created</div>
            </div>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="import-errors">
            <h4>Errors ({errors.length}):</h4>
            <ul>
              {errors.slice(0, 5).map((error, index) => (
                <li key={index} className="error-item">
                  <strong>Row {error.row}:</strong> {error.message}
                </li>
              ))}
            </ul>
            {errors.length > 5 && (
              <p className="more-errors">... and {errors.length - 5} more errors</p>
            )}
          </div>
        )}

        {warnings.length > 0 && (
          <div className="import-warnings">
            <h4>Warnings ({warnings.length}):</h4>
            <ul>
              {warnings.slice(0, 3).map((warning, index) => (
                <li key={index} className="warning-item">
                  <strong>Row {warning.row}:</strong> {warning.message}
                </li>
              ))}
            </ul>
            {warnings.length > 3 && (
              <p className="more-warnings">... and {warnings.length - 3} more warnings</p>
            )}
          </div>
        )}

        <div className="complete-actions">
          <button className="primary-btn" onClick={handleComplete}>
            Continue
          </button>
        </div>
      </div>
    );
  };

  const renderErrorStep = () => (
    <div className="import-step error-step">
      <div className="error-header">
        <div className="error-icon">❌</div>
        <h3>Import Failed</h3>
      </div>
      
      <div className="error-message">
        <p>{error}</p>
      </div>
      
      <div className="error-actions">
        <button className="secondary-btn" onClick={handleTryAgain}>
          Try Again
        </button>
        <button className="cancel-btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className={`import-modal theme-${theme}`}>
        <div className="modal-header">
          <h2>Import KPIs from CSV</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-content">
          {currentStep === 'upload' && renderUploadStep()}
          {currentStep === 'preview' && renderPreviewStep()}
          {currentStep === 'importing' && renderImportingStep()}
          {currentStep === 'complete' && renderCompleteStep()}
          {currentStep === 'error' && renderErrorStep()}
        </div>
      </div>
    </div>
  );
};

export default KPIImportModal;
